// Gate component — activates PIN lock hooks and blocks initial render only
// until PIN status is known. The lock's *blocking* effect is scoped to the
// financial routes by FinancialLockGate; this gate no longer hides the whole
// app when locked. It also drops cached financial data when the lock engages.

import { ReactNode, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePinLock } from '@/hooks/usePinLock';
import { useLockStore } from '@/stores/lockStore';
import { useAuth } from '@/hooks/useAuth';
import { clearFinancialQueries } from '@/features/lock/financialQueryKeys';

interface AppLockGateProps {
  children: ReactNode;
}

export function AppLockGate({ children }: AppLockGateProps) {
  const { isAuthenticated } = useAuth();
  const isLocked = useLockStore((s) => s.isLocked);
  const pinStatusChecked = useLockStore((s) => s.pinStatusChecked);
  const queryClient = useQueryClient();

  // Activate all PIN lock hooks (idle detection, visibility lock, status check)
  usePinLock();

  // Drop cached financial data whenever the lock engages, so a locked finance
  // section leaves nothing sensitive in memory. Hub summary and non-financial
  // caches are intentionally preserved (see financialQueryKeys.ts).
  useEffect(() => {
    if (isAuthenticated && isLocked) {
      clearFinancialQueries(queryClient);
    }
  }, [isAuthenticated, isLocked, queryClient]);

  // Block rendering until we know whether PIN is enabled. This prevents a
  // flash of financial content before the route gate can lock it.
  if (isAuthenticated && !pinStatusChecked) {
    return null;
  }

  return <>{children}</>;
}
